import { Button, Modal, Spinner } from "@heroui/react";
import { isAxiosError } from "axios";
import { TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";

import { SelectableScrapedProductCard } from "./product-card";

import { ImageTile, notify, SearchBox } from "@/components/kit";
import { useEditProduct } from "@/hooks/api/product/useEditProduct";
import { useGetScrapedProducts } from "@/hooks/api/scraped-product/useGetScrapedProducts";
import { useDebounce } from "@/hooks/useDebounce";
import { Product } from "@/types/product";

interface ChangePrimaryScrapedProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

export const ChangePrimaryScrapedProductModal = ({
  isOpen,
  onClose,
  product,
}: ChangePrimaryScrapedProductModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedScrapedProductId, setSelectedScrapedProductId] = useState<
    string | null
  >(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Auto-select current primary when modal opens
  useEffect(() => {
    if (isOpen && product?.scrapedProductId) {
      setSelectedScrapedProductId(product.scrapedProductId);
    }
  }, [isOpen, product?.scrapedProductId]);

  const {
    data: scrapedProductsData,
    isPending: isLoading,
    isError,
  } = useGetScrapedProducts({
    name: debouncedSearchTerm,
    limit: 12,
    page: 1,
  });

  const { mutate: editProduct, isPending: isUpdating } = useEditProduct();

  const handleConfirm = () => {
    if (!selectedScrapedProductId || !product) return;

    editProduct(
      {
        id: product.id,
        scrapedProductId: selectedScrapedProductId,
      },
      {
        onSuccess: () => {
          notify.success(
            "Primary Scraped Product Updated",
            "The primary scraped product link has been updated successfully",
          );
          handleClose();
        },
        onError: (error) => {
          let message = "Please try again and contact support";

          if (isAxiosError(error)) {
            message = error.response?.data?.message || message;
          }

          notify.error("Update Failed", message);
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
    setSelectedScrapedProductId(null);
    setSearchTerm("");
  };

  if (!product) return null;

  const scrapedProducts = scrapedProductsData?.data || [];
  const currentPrimaryId = product.scrapedProductId;

  const others = scrapedProducts.filter((sp) => sp.id !== currentPrimaryId);

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      variant="opaque"
      onOpenChange={(open) => !open && handleClose()}
    >
      <Modal.Container placement="center" scroll="inside">
        <Modal.Dialog className="w-full max-w-2xl">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>Change Primary Scraped Product</Modal.Heading>
            <p className="text-muted text-sm">
              Select a new primary scraped product for &ldquo;{product.name}
              &rdquo;
            </p>
          </Modal.Header>
          <Modal.Body className="flex flex-col gap-4 p-1">
            <div className="bg-surface-secondary flex items-start gap-3 rounded-2xl p-3">
              <ImageTile src={product.images?.[0]} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{product.name}</p>
                <p className="text-muted text-xs">{product.supplier.name}</p>
                {product.scrapedProduct ? (
                  <p className="text-accent mt-1 text-xs">
                    Primary:{" "}
                    <span className="font-semibold">
                      {product.scrapedProduct.name}
                    </span>
                  </p>
                ) : (
                  <p className="text-muted mt-1 text-xs italic">
                    No primary link set
                  </p>
                )}
              </div>
            </div>

            <SearchBox
              className="w-full"
              placeholder="Search scraped products"
              value={searchTerm}
              onChange={setSearchTerm}
            />

            <div className="min-h-64">
              {isLoading && (
                <div className="flex h-64 items-center justify-center gap-2">
                  <Spinner size="sm" />
                  <span className="text-muted text-sm">
                    Loading scraped products...
                  </span>
                </div>
              )}

              {!isLoading && isError && (
                <div className="flex h-64 flex-col items-center justify-center gap-2 text-center">
                  <TriangleAlert className="text-(--danger) size-8" />
                  <p className="text-(--danger) text-sm">
                    Failed to load scraped products
                  </p>
                </div>
              )}

              {!isLoading && !isError && (
                <div
                  className="flex max-h-[400px] flex-col gap-4 overflow-y-auto"
                  role="radiogroup"
                >
                  {product.scrapedProduct && (
                    <div className="flex flex-col gap-1">
                      <p className="text-muted px-3 text-xs font-medium">
                        Current Primary
                      </p>
                      <div
                        aria-checked={
                          selectedScrapedProductId === product.scrapedProductId
                        }
                        className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 outline-none focus-visible:ring-2 focus-visible:ring-(--focus) ${
                          selectedScrapedProductId === product.scrapedProductId
                            ? "bg-accent/10"
                            : "hover:bg-surface-secondary"
                        }`}
                        role="radio"
                        tabIndex={0}
                        onClick={() =>
                          setSelectedScrapedProductId(product.scrapedProductId!)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedScrapedProductId(
                              product.scrapedProductId!,
                            );
                          }
                        }}
                      >
                        <span
                          aria-hidden="true"
                          className={`flex size-4 shrink-0 items-center justify-center rounded-full border ${
                            selectedScrapedProductId ===
                            product.scrapedProductId
                              ? "border-accent bg-accent"
                              : "border-border"
                          }`}
                        >
                          {selectedScrapedProductId ===
                            product.scrapedProductId && (
                            <span className="size-1.5 rounded-full bg-white" />
                          )}
                        </span>
                        <ImageTile />
                        <p
                          className="min-w-0 flex-1 truncate text-sm font-medium"
                          title={product.scrapedProduct.name}
                        >
                          {product.scrapedProduct.name}
                        </p>
                      </div>
                    </div>
                  )}

                  {others.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <p className="text-muted px-3 text-xs font-medium">
                        {searchTerm ? "Search Results" : "Other Products"}
                      </p>
                      {others.map((scrapedProduct) => (
                        <SelectableScrapedProductCard
                          key={scrapedProduct.id}
                          isPrimary={false}
                          isSelected={
                            selectedScrapedProductId === scrapedProduct.id
                          }
                          scrapedProduct={scrapedProduct}
                          onSelect={() =>
                            setSelectedScrapedProductId(scrapedProduct.id)
                          }
                        />
                      ))}
                    </div>
                  )}

                  {!product.scrapedProduct && scrapedProducts.length === 0 && (
                    <div className="flex h-64 flex-col items-center justify-center gap-1 text-center">
                      <p className="font-medium">No scraped products found</p>
                      <p className="text-muted text-sm">
                        Try adjusting your search
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              isDisabled={isUpdating}
              variant="ghost"
              onPress={handleClose}
            >
              Cancel
            </Button>
            <Button
              isDisabled={!selectedScrapedProductId || isUpdating}
              isPending={isUpdating}
              onPress={handleConfirm}
            >
              Update Primary Link
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
};
